import { useState, useEffect } from 'react';

interface BrushSizeSliderProps {
  readonly className?: string;
  readonly isActive?: boolean;
  readonly brushSize?: number;
  readonly onBrushSizeChange?: (size: number) => void;
  readonly minSize?: number;
  readonly maxSize?: number;
  readonly readOnly?: boolean;
}

export function BrushSizeSlider({ 
  className = "", 
  isActive = false, 
  brushSize = 20,
  onBrushSizeChange,
  minSize = 5,
  maxSize = 100,
  readOnly = false
}: BrushSizeSliderProps) {
  const [currentSize, setCurrentSize] = useState(brushSize);

  // Sync internal state with brushSize prop when it changes
  useEffect(() => {
    setCurrentSize(brushSize);
  }, [brushSize]);

  const handleSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = Number.parseInt(event.target.value, 10);
    setCurrentSize(newSize);
    if (onBrushSizeChange) {
      onBrushSizeChange(newSize);
    }
  };

  return (
    <div className={`relative px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 ${className}`}>
      <div className="flex items-center justify-between">
        {/* Width label - Left side */}
        <div className={`flex items-center space-x-2 ${isActive ? '' : 'opacity-50'}`}>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
            Width:
          </span>
          <span className="text-sm text-gray-600 dark:text-gray-400 min-w-[3ch] text-right">
            {currentSize}px
          </span>
        </div>

        {/* Visual brush preview - Dead center */}
        <div className={`absolute left-1/2 transform -translate-x-1/2 flex items-center justify-center w-12 h-8 ${isActive ? '' : 'opacity-50'}`}>
          <div 
            className={`rounded-full border-2 ${
              isActive 
                ? 'border-blue-500 bg-blue-100 dark:bg-blue-900/30' 
                : 'border-gray-400 bg-gray-100 dark:bg-gray-700'
            }`}
            style={{
              width: `${Math.max(6, Math.min(24, currentSize / 4))}px`,
              height: `${Math.max(6, Math.min(24, currentSize / 4))}px`
            }}
          />
        </div>

        {/* Slider - All the way to the right */}
        <div className="w-48">
          <input
            type="range"
            min={minSize}
            max={maxSize}
            value={currentSize}
            onChange={handleSizeChange}
            disabled={!isActive || readOnly}
            className={`w-full h-2 rounded-lg appearance-none cursor-pointer transition-opacity ${
              !isActive || readOnly
                ? 'opacity-30 cursor-not-allowed' 
                : 'opacity-100'
            } slider-thumb`}
            style={{
              background: isActive && !readOnly 
                ? `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((currentSize - minSize) / (maxSize - minSize)) * 100}%, #d1d5db ${((currentSize - minSize) / (maxSize - minSize)) * 100}%, #d1d5db 100%)`
                : '#d1d5db'
            }}
          />
        </div>
      </div>
    </div>
  );
}
